import { selectUserInfo } from "features/user/userSlice";
import { Grid, InputAdornment, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BankAccountProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Bank } from "assets/SideBarIcons/bank.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import { GetBankAccountByIdResponse } from "@subModule/src/services/bankaccount/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import { useGetPaymentModesDropdownService } from "subModule/src/services/payment/usePayments";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}
export default function BankAccountModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      type: { value: 20, label: t("bank") },
    },
  });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedAccountTitlePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phAccountTitleAr") : t("phAccountTitleEn");
  const localizedBankNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phBankNameAr") : t("phBankNameEn");
  const loggedInUserCurrency = loggedInUserData?.business?.currency;
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "accounts",
    moduleHref: "/accounts",
    entityFallbackLabelKey: "accountTitle",
    isEdit: !!params?.id,
  });
  const accountTypes = [
    { value: 10, label: t("cash") },
    { value: 20, label: t("bank") },
  ];
  const { data: paymentModesData, refetch: paymentModesRefetch } =
    useGetPaymentModesDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const paymentModes = (paymentModesData?.pages[0]?.data?.content || []).map(
    (item: any) => ({ value: item.id, label: item.name }),
  );
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    BankAccountProfileService.getBankAccountByID(+(params as any)?.id)
      .then((res: GetBankAccountByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            type: res?.data?.type
              ? {
                  value: res?.data?.type,
                  label:
                    accountTypes.find((v) => v.value === res?.data.type)
                      ?.label || res.data.type,
                }
              : null,
            paymentMode: res?.data?.paymentMode
              ? {
                  value: res?.data?.paymentMode,
                  label:
                    res?.data?.paymentModeTitle || res?.data?.paymentMode,
                }
              : null,
            openingBalance: +(res?.data?.openingBalance || 0),
          });
          captureLabel(res?.data?.accountTitle);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext" | "open",
    search?: string,
  ) => {
    if (action === "open") {
      setDropdownPage(1);
      setDropdownSearchTerm("");
      setSearchLoading(typeName);
    } else if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "paymentModes")
        paymentModesRefetch().finally(() => setSearchLoading(null));
    }, action === "open" ? 0 : 300);
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const payload = {
      ...data,
      type: data?.type?.value || 0,
      paymentMode: data?.paymentMode?.value || null,
      openingBalance: +data?.openingBalance || 0,
    };
    if (params?.id) {
      BankAccountProfileService.updateBankAccount(+params?.id, payload)
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.accountTitle);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("ACCOUNT") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
          setIsBtnEnabled(true);
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      BankAccountProfileService.createBankAccount(payload)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("ACCOUNT") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editAccount") : t("addAccount")}
        icon={<Bank height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_BANKACCOUNT_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? "editAccount" : "addAccount"}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          content={
            <Grid
              container
              component="form"
              id="form"
              className="items-baseline"
              spacing={3}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid item container xs={12} spacing={3}>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="type"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("type") + " *"}
                        {...field}
                        options={accountTypes}
                        isSearchable
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          if (selectedOption?.value === 10) {
                            setValue("bankName", null);
                            setValue("bankNameSecondary", null);
                            setValue("accountNumber", null);
                            setValue("iban", null);
                          }
                        }}
                        error={errors.type?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="paymentMode"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("PaymentMode") + " *"}
                        {...field}
                        options={paymentModes}
                        isSearchable
                        onMenuOpen={() =>
                          handleDropdownActions("paymentModes", "open")
                        }
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        error={errors.paymentMode?.message as string}
                        isLoading={searchLoading === "paymentModes"}
                        onInputChange={(
                          search: string,
                          { action }: { action: string },
                        ) => {
                          if (
                            action === "input-change" ||
                            (action !== "set-value" &&
                              action !== "menu-close" &&
                              action !== "input-blur")
                          )
                            handleDropdownActions(
                              "paymentModes",
                              "searchIt",
                              search,
                            );
                          else if (
                            action === "input-blur" ||
                            action === "menu-close"
                          ) {
                            if (dropdownSearchTerm || dropdownPage > 1)
                              handleDropdownActions(
                                "paymentModes",
                                "searchIt",
                                "",
                              );
                            else setDropdownSearchTerm("");
                            setDropdownPage(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {
                          if (!(paymentModesData?.pages[0]?.data as any)?.last)
                            handleDropdownActions("paymentModes", "loadNext");
                        }}
                        page={dropdownPage}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("accountTitle", {
                    validate: {
                      required: (value) =>
                        (value && value.trim() !== "") ||
                        t("This field is required."),
                    },
                  })}
                  label={`${!primaryLanguage ? t("primary") : ""} ${t("accountTitle")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`}
                  placeholder={localizedAccountTitlePlaceholder(primaryLanguage)}
                  title={`${!primaryLanguage ? t("primary") : ""} ${t("accountTitle")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.accountTitle}
                  helperText={errors.accountTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("secondaryAccountTitle")}
                  label={`${!secondaryLanguage ? t("secondary") : ""} ${t("accountTitle")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`}
                  placeholder={localizedAccountTitlePlaceholder(secondaryLanguage)}
                  title={`${!secondaryLanguage ? t("secondary") : ""} ${t("accountTitle")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.secondaryAccountTitle}
                  helperText={errors.secondaryAccountTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {watch("type")?.value === 10 ? null : (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("bankName", {
                        validate: {
                          required: (value) =>
                            (value && value.trim() !== "") ||
                            t("This field is required."),
                        },
                      })}
                      label={`${!primaryLanguage ? t("primary") : ""} ${t("bankName")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`}
                      placeholder={localizedBankNamePlaceholder(primaryLanguage)}
                      title={`${!primaryLanguage ? t("primary") : ""} ${t("bankName")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`
                        .replace(/\s+/g, " ")
                        .trim()}
                      fullWidth
                      error={!!errors.bankName}
                      helperText={errors.bankName?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("bankNameSecondary")}
                      label={`${!secondaryLanguage ? t("secondary") : ""} ${t("bankName")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`}
                      placeholder={localizedBankNamePlaceholder(secondaryLanguage)}
                      title={`${!secondaryLanguage ? t("secondary") : ""} ${t("bankName")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`
                        .replace(/\s+/g, " ")
                        .trim()}
                      fullWidth
                      error={!!errors.bankNameSecondary}
                      helperText={errors.bankNameSecondary?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("accountNumber", {
                        validate: {
                          required: (value) =>
                            (value && value.trim() !== "") ||
                            t("This field is required."),
                        },
                      })}
                      label={t("accountNumber")}
                      placeholder={t("phAccountNumber")}
                      title={t("accountNumber")}
                      fullWidth
                      error={!!errors.accountNumber}
                      helperText={errors.accountNumber?.message as string}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("iban", {
                        validate: {
                          required: (value) =>
                            (value && value.trim() !== "") ||
                            t("This field is required."),
                        },
                      })}
                      label={t("IBAN")}
                      placeholder={t("phIBAN")}
                      title={t("IBAN")}
                      fullWidth
                      error={!!errors.iban}
                      helperText={errors.iban?.message as string}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  type="number"
                  {...register("openingBalance", {
                    required: false,
                    validate: (value) =>
                      !value || value >= 0 || t("onlyPositiveNumbers"),
                  })}
                  label={t("openingBalance")}
                  placeholder={t("phOpeningBalance")}
                  title={t("openingBalance")}
                  fullWidth
                  error={!!errors.openingBalance}
                  helperText={errors.openingBalance?.message as string}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: loggedInUserCurrency ? (
                      <InputAdornment position="end">
                        <span className="font-Saira text-[13px]">
                          {loggedInUserCurrency?.toUpperCase()}
                        </span>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                      {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    "& .Mui-disabled": { opacity: 0.5 },
                  }}
                  disabled={(params as any)?.id}
                />
              </Grid>
            </Grid>
          }
        />
      )}
    </>
  );
}
